import React from 'react';
import {Button, Divider} from 'antd-mobile';
import { useTranslation } from 'react-i18next';
import { getPodStatusInfo, isUrgentStatus } from '../utils/podStatusUtils';
import { formatLocalizedDateTime} from '../utils/dateFormatUtils';
import InfoItem from '../pages/delivery/components/InfoItem';
import {isDspManager} from "../utils/auth";
import { getTranslatedPodReviewMessage } from '../utils/podReviewMessageUtils';
import { PodReviewMessageItemType } from '../pages/delivery/graphql/types/DeliveryTaskOrderType';

export interface PodReviewCardProps {
  trackingNumber: string;
  deliveredAt: number | null;
  podReviewStatus: number;
  podReviewMessage: PodReviewMessageItemType[] | null;
  deliveredDriverAccountName?: string;
  podHasBeenManualDisapproved?: boolean;
  isModifiedPodPhotos?: boolean;
  address?: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    zipcode: string;
  };
  onAction: (trackingNumber: string) => void;
}

// 定义已知的错误类型
const KNOWN_ERROR_TYPES = ['NO_LABEL_PHOTO', 'WRONG_TRK', 'NO_HOUSE_APT', 'WRONG_HOUSE_APT'];

export const PodReviewCard: React.FC<PodReviewCardProps> = ({
  trackingNumber,
  deliveredAt,
  podReviewStatus,
  podReviewMessage,
  deliveredDriverAccountName,
  podHasBeenManualDisapproved,
  isModifiedPodPhotos,
  onAction
}) => {
  const { t } = useTranslation();
  const statusInfo = getPodStatusInfo(podReviewStatus);
  
  // 直接在组件内计算modifiableDeadline
  const deliveredAtNum = deliveredAt ? Number(deliveredAt) : 0;
  const modifiableDeadline = deliveredAt ? new Date((deliveredAtNum + 24 * 3600) * 1000) : new Date();
  const isUrgent = deliveredAt ? isUrgentStatus(deliveredAtNum) : false;
  
  // 检查是否超过修改deadline
  const isOverDeadline = deliveredAt ? new Date() > modifiableDeadline : false;

  // 将 podReviewMessage 数组转换为国际化的 requirement 字符串
  const getInternationalizedRequirements = (messages: string[] | null): string => {
    if (!messages || messages.length === 0) {
      return '';
    }

    const translatedRequirements = messages
      .map(message => {
        // 只对已知错误类型进行国际化翻译
        if (KNOWN_ERROR_TYPES.includes(message)) {
          const key = `pages.delivery.podReview.requirements.${message}`;
          return t(key, { defaultValue: message });
        }
        // 其他类型直接返回原值
        return '';
      })
      .filter(requirement => requirement !== ''); // 过滤掉空字符串
    
    // 使用逗号分隔所有语言
    return translatedRequirements.join(', ');
  };
  // 检查是否只有一条消息且不属于已知错误类型
  const isOnlyOtherType = (messages: string[] | null): boolean => {
    if (!messages || messages.length === 0) return false;
    return messages.length === 1 && !KNOWN_ERROR_TYPES.includes(messages[0]);
  };

  // 获取完整的 actionDescription，支持HTML格式
  const getActionDescription = (): string => {
    // 没有驳回原因，不展示
    if (!podReviewMessage || podReviewMessage.length === 0) {
      return '';
    }
    
    // 获取翻译后的消息字符串数组
    const translatedMessages = getTranslatedPodReviewMessage(podReviewMessage) || [];
    
    if (isDspManager()) {
      // 如果只有一条消息且不属于已知错误类型，显示特殊消息
      if (isOnlyOtherType(translatedMessages)) {
        return t('pages.delivery.podReview.manageOtherRequiredAction');
      }

      const requirements = getInternationalizedRequirements(translatedMessages);
      const baseMessage = t('pages.delivery.podReview.manageRequiredAction', { requirement: '{{requirement}}' });
      return baseMessage.replace('{{requirement}}', `<span class="font-bold text-red-600">${requirements}</span>`);
    } else {
      // 如果只有一条消息且不属于已知错误类型，显示特殊消息
      if (isOnlyOtherType(translatedMessages)) {
        return t('pages.delivery.podReview.otherDeadlineMessage');
      }

      const requirements = getInternationalizedRequirements(translatedMessages);
      const baseMessage = t('pages.delivery.podReview.actionDescription', { requirement: '{{requirement}}' });
      return baseMessage.replace('{{requirement}}', `<span class="font-bold text-red-600">${requirements}</span>`);
    }
  };
  
  const handleButtonClick = () => {
    onAction(trackingNumber);
  };

  const getCardBackgroundColor = () => {
    if (statusInfo.status === 4) { // Manual Approved
      return 'bg-gray-100';
    }
    if (isUrgent) { // 距离截止时间小于60分钟
      return 'bg-red-50';
    }
    return 'bg-white';
  };

  const getCardContentBackgroundColor = () => {
    if (statusInfo.status === 4) { // Manual Approved
      return 'bg-gray-200';
    }
    if (isUrgent) { // 距离截止时间小于60分钟
      return 'bg-red-50';
    }
    return 'bg-white';
  };
  // 右边上角的角标
  const getStatusText = () => {
    if (statusInfo.label === 'Manual Approved') {
      return t('pages.delivery.podReview.approved');
    } else if (statusInfo.label === 'Manual Pending') {
      return t('pages.delivery.podReview.modify');
    } else {
      return t('pages.delivery.podReview.disapproved');
    }
  };

  // 获取按钮文字
  const getButtonText = () => {
    // DSP经理：超过deadline显示"查看"，否则显示"去修改"
    if (isDspManager()) {
      return isOverDeadline
        ? t('pages.delivery.podReview.view')
        : t('pages.delivery.podReview.goModify');
    }

    // 司机：超过deadline或已到达修改次数上限时显示"查看"
    const hasReachedModificationLimit = podHasBeenManualDisapproved && isModifiedPodPhotos;
    if (isOverDeadline || hasReachedModificationLimit) {
      return t('pages.delivery.podReview.view');
    }

    // 其他情况使用状态对应的按钮文字
    return t(statusInfo.buttonTextKey);
  };


  return (
    <div className={`border border-gray-200 rounded-lg overflow-hidden mb-3 ${getCardBackgroundColor()}`}>
      {/* 卡片头部 */}
      <div className={`p-4 ${getCardContentBackgroundColor()}`}>
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1">
            <div className="font-semibold text-lg text-gray-900 mb-1">
              POD {t('pages.delivery.podReview.manualDisapproved')}
            </div>
          </div>
          <div className="ml-2">
            <span className={`px-2 py-1 rounded text-xs font-medium ${
              statusInfo.label === 'Manual Approved' 
                ? 'bg-green-100 text-green-700' 
                : statusInfo.label === 'Manual Pending'
                ? 'bg-orange-100 text-orange-700'
                : 'bg-red-100 text-red-700'
            }`}>
              {getStatusText()}
            </span>
          </div>
        </div>
        <Divider/>
        {/* 使用 InfoItem 组件显示信息 */}
        <div className="space-y-1">
          <InfoItem
            label={t('pages.delivery.podReview.trackingNumber')}
            value={trackingNumber}
          />

          {/*违规司机名 - 只有DSP经理才显示*/}
          {isDspManager() && deliveredDriverAccountName && (
            <InfoItem
                label={t('pages.delivery.podReview.driverWithViolations')}
                value={deliveredDriverAccountName}
            />
          )}

          {/*驳回原因*/}
          <div className="flex justify-between py-2 border-gray-100">
            <span className="text-gray-600 text-sm">{t('pages.delivery.podReview.disapproveReason')}:</span>
            <span className="text-sm font-medium text-left break-all max-w-[70%]">
              {(() => {
                const translatedMessages = getTranslatedPodReviewMessage(podReviewMessage) || [];
                return translatedMessages.map(msg => {
                  if (KNOWN_ERROR_TYPES.includes(msg)) {
                    return t(`pages.delivery.podReview.errorTypes.${msg}`, { defaultValue: msg });
                  }
                  return msg;
                }).join(', ');
              })()}
            </span>
         </div>

          {/* 要求操作 - 人工通过时不展示 */}
          {statusInfo.status !== 4 && (
            <div className="flex justify-between py-2 border-gray-100">
              <span className="text-gray-600 text-sm">{t('pages.delivery.podReview.requiredAction')}:</span>
              <span
                className="text-sm font-medium text-left break-all max-w-[70%]"
                dangerouslySetInnerHTML={{ __html: getActionDescription() }}
              />
            </div>
          )}

          {/*可修改期限 - 人工通过时不展示*/}
          {statusInfo.status !== 4 && (
            <div className="flex justify-between py-2 border-gray-100">
              <span className="text-gray-600 text-sm">{t('pages.delivery.podReview.modifiableDeadline')}:</span>
              <span className="text-sm font-bold text-right text-red-600">
                   {modifiableDeadline instanceof Date && !isNaN(modifiableDeadline.getTime())
                       ? formatLocalizedDateTime(modifiableDeadline)
                       : 'Invalid Date'}
              </span>
            </div>
          )}
        </div>

        {/* 操作按钮 */}
        <Button
          color="primary"
          fill="solid"
          className="w-full"
          onClick={handleButtonClick}
        >
          {getButtonText()}
        </Button>
      </div>
    </div>
  );
};
